import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { instance } from '../axios/AxiosInstance';

const TOKEN_KEY = 'glubesAppToken';

export interface User {
  email: string;
  phone: string;
  role: string;
  first_name: string;
  last_name: string;
  active: boolean;
  created_at: string;
  version?: string;
}

interface UserContextValue {
  user: User | null;
  setUser: (user: User | null) => void;
  clearUser: () => void;
  loadingUser: boolean;
}

const UserContext = createContext<UserContextValue | null>(null);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const setUser = useCallback((value: User | null) => {
    setUserState(value);
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setLoadingUser(false);
      return;
    }
    if (user !== null) {
      setLoadingUser(false);
      return;
    }
    instance
      .get('users')
      .then((res) => setUserState(res.data.user))
      .catch(() => setUserState(null))
      .finally(() => setLoadingUser(false));
  }, [user]);

  const value: UserContextValue = {
    user,
    setUser,
    clearUser,
    loadingUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser(): UserContextValue {
  const ctx = useContext(UserContext);
  if (ctx == null) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return ctx;
}
